/**
 * Layout Component - רכיב פריסה
 * Main layout with navigation and header for authenticated pages
 */

import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import {
  LayoutDashboard,
  MessageSquare,
  ClipboardList,
  User,
  LogOut,
  Menu,
  X,
  Shield,
  CreditCard,
  GraduationCap,
  Download,
  Home,
} from 'lucide-react'
import { useState } from 'react'
import SkipLink from './SkipLink'
import Footer from './Footer'
import usePwaInstall from '../../hooks/usePwaInstall'
import {
  TRACKS,
  PUBLIC_TRACK_KEYS,
  isPortalOnlyUser,
  managedTracks,
  MAZAN,
  hasMazanAccess,
  managesMazan,
  type TrackKey,
} from '../../config/tracks'

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Pointless to offer the install guide inside the already-installed app.
  const { isStandalone } = usePwaInstall()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Free areas the user has actually joined. Staff see them all so they can
  // check what a visitor sees without a second account.
  // A section admin previews only the areas they run — the same rule the server
  // applies, so the menu can't offer a link that 403s.
  const isSectionAdmin = user?.role === 'sub_admin'
  const isStaff = user?.role === 'admin' || user?.role === 'advisor'
  const toNavItem = (key: TrackKey) => {
    const TrackIcon = TRACKS[key].Icon
    return {
      to: TRACKS[key].basePath,
      icon: <TrackIcon size={20} aria-hidden="true" />,
      label: TRACKS[key].name,
    }
  }

  const trackNavItems = PUBLIC_TRACK_KEYS.filter(
    (key) => isStaff || user?.portalAccess?.some((p) => p.track === key)
  ).map(toNavItem)

  // מאזן — the paid report area, for those who joined it, full admins, and the
  // section admin who runs it (they use it as a customer too, beside the panel).
  const mazanNavItems =
    managesMazan(user) || hasMazanAccess(user)
      ? [{ to: MAZAN.homePath, icon: <MAZAN.Icon size={20} aria-hidden="true" />, label: MAZAN.shortName }]
      : []

  // Includes the student area (/learn), which is not a public self-signup track
  // and so never appears in the list above.
  const sectionAdminNavItems = managedTracks(user).map(toNavItem)

  /**
   * A /pensia or /mashkanta user who never engaged with the advisory product gets
   * the student treatment: their area, profile, install, sign out — nothing else.
   *
   * Keyed off the ACCOUNT, not the current URL: a menu that shrinks and grows as
   * you move between /pensia and /profile is worse than either menu on its own.
   */
  const portalOnly = isPortalOnlyUser(user)

  // Students (learning tool) get a minimal, education-only menu.
  const navItems = isSectionAdmin
    ? [
        // A section admin is staff, not a client: the advisory chrome (לוח בקרה,
        // שאלון סיכון, צ'אט ייעוץ, המנוי שלי) is not theirs to use. They get the
        // panel, the areas they run, and their profile.
        {
          to: '/admin',
          icon: <Shield size={20} aria-hidden="true" />,
          label: 'ניהול',
        },
        ...sectionAdminNavItems,
        ...mazanNavItems,
        {
          to: '/profile',
          icon: <User size={20} aria-hidden="true" />,
          label: 'פרופיל',
        },
      ]
    : user?.role === 'student'
    ? [
        {
          to: '/learn',
          icon: <GraduationCap size={20} aria-hidden="true" />,
          label: 'מסלול הלמידה',
        },
        {
          to: '/profile',
          icon: <User size={20} aria-hidden="true" />,
          label: 'פרופיל',
        },
      ]
    : portalOnly
    ? [
        // The area(s) they joined, then profile — same shape as the student menu.
        ...trackNavItems,
        ...mazanNavItems,
        {
          to: '/profile',
          icon: <User size={20} aria-hidden="true" />,
          label: 'פרופיל',
        },
      ]
    : [
    {
      to: '/',
      icon: <Home size={20} aria-hidden="true" />,
      label: 'דף הבית',
    },
    {
      to: '/dashboard',
      icon: <LayoutDashboard size={20} aria-hidden="true" />,
      label: 'לוח בקרה',
    },
    {
      to: '/questionnaire',
      icon: <ClipboardList size={20} aria-hidden="true" />,
      label: 'שאלון סיכון',
    },
    {
      to: '/chat',
      icon: <MessageSquare size={20} aria-hidden="true" />,
      label: 'צ\'אט ייעוץ',
    },
    {
      to: '/profile',
      icon: <User size={20} aria-hidden="true" />,
      label: 'פרופיל',
    },
    {
      to: '/subscription',
      icon: <CreditCard size={20} aria-hidden="true" />,
      label: 'המנוי שלי',
    },
    // Free areas this user has joined (תכנון פרישה / משכנתא)
    ...trackNavItems,
    ...mazanNavItems,
    // Admin/Advisor only menu
    ...(user?.role === 'admin' || user?.role === 'advisor'
      ? [
          {
            to: '/admin',
            icon: <Shield size={20} aria-hidden="true" />,
            label: 'ניהול',
          },
        ]
      : []),
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <SkipLink />

      {/* Mobile sidebar backdrop — closes the menu, exposed as a real button for keyboard users */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="סגירת תפריט הניווט"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        id="main-nav"
        aria-label="ניווט ראשי"
        className={`fixed top-0 right-0 h-full w-64 bg-white border-l border-gray-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-2xl font-heading font-bold text-primary-600">
                יפתח ונגר יועץ השקעות
              </p>
              <button
                onClick={() => setSidebarOpen(false)}
                aria-label="סגירת תפריט הניווט"
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">אזור אישי</p>
          </div>

          {/* Navigation */}
          <nav aria-label="ניווט ראשי" className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}

            {!isStandalone && (
              <NavLink
                to="/install"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`
                }
              >
                <Download size={20} aria-hidden="true" />
                <span>התקנת האפליקציה</span>
              </NavLink>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User size={20} className="text-primary-600" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={18} aria-hidden="true" />
              <span>התנתק</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:mr-64 min-h-screen flex flex-col">
        {/* Mobile header */}
        <header className="lg:hidden relative z-10 bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xl font-heading font-bold text-primary-600">
              יפתח ונגר יועץ השקעות
            </p>
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="פתיחת תפריט הניווט"
              aria-expanded={sidebarOpen}
              aria-controls="main-nav"
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu size={24} aria-hidden="true" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" tabIndex={-1} className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>

        <Footer />
      </div>
    </div>
  )
}
